// stores/reaction.store.ts
import { defineStore } from 'pinia';
import { ref, computed, reactive } from 'vue';

import { TrackingEvents } from '@/helpers/enum';
import { trackEvent, trackError } from '@/helpers/tracking.helpers';
import httpService from '@/services/http.service';
import storageService from '@/services/storage.service';
import {
  Unsubscribe,
  collection,
  query,
  where,
  onSnapshot,
  getFirestore,
} from 'firebase/firestore';
import { ReactionCountModel } from '@/views/private/timeline/models/reactioncount.model';
import { CommentCountModel } from '@/views/private/timeline/models/commentcount.model';
import { LikePostModel } from '@/views/private/timeline/models/likepost.model';
import PollCountModel from '@/views/private/timeline/models/pollcount.model';
import { RepostCountModel } from '@/views/private/timeline/models/repostcount.model';
import { UnlikeModel } from '@/views/private/timeline/models/unlike.model';
import utilService from './util.service';
import { TYPE } from 'vue-toastification';



export const useReactionStore = defineStore('reactions', () => {
  const reactionCountsVersion = ref(0);
  const lastAffectedPostIds = ref<Set<string>>(new Set());

  const optimisticReactions = reactive({
    map: new Map<string, { reactionType: string; timestamp: number }>()
  });

  const myReactions = reactive({
    map: new Map<string, string>()
  });

  // State - Using reactive wrappers for Maps
  const reactiveReactionCounts = reactive({
    map: new Map<string, ReactionCountModel>()
  });

  const reactiveCommentCounts = reactive({
    map: new Map<string, CommentCountModel>()
  });

  const reactiveRepostCounts = reactive({
    map: new Map<string, RepostCountModel>()
  });

  const reactivePollCounts = reactive({
    map: new Map<string, PollCountModel>()
  });

  const unsubscribeReactions = ref<Unsubscribe[]>([]);
  const subscribedPostIds = ref<Set<string>>(new Set());
  const currentFeedId = ref<string | null>(null);
  const FIRESTORE_IN_QUERY_LIMIT = 30;

  const reactionKey = (sourceId: string, sourceType: string) =>
    `${sourceId}-${sourceType}`;

  const chunkPostIds = (postIds: string[], size = FIRESTORE_IN_QUERY_LIMIT) => {
    const chunks: string[][] = [];
    for (let i = 0; i < postIds.length; i += size) {
      chunks.push(postIds.slice(i, i + size));
    }
    return chunks;
  };

  const processFeedCountSnapshot = (querySnapshot: any) => {
    querySnapshot.docChanges().forEach((doc: any) => {
      try {
        const activityData = doc.doc.data();

        switch (activityData.type) {
          case "reaction-count":
            handleReactionCountChange(activityData, doc.type);
            break;
          case "comment-count":
            handleCommentCountChange(activityData, doc.type);
            break;
          case "repost-count":
            handleRepostCountChange(activityData, doc.type);
            break;
          case "poll-chart-count":
            handlePollCountChange(activityData, doc.type);
            break;
        }
      } catch (e) {
        console.error("💥 Error processing reaction update:", e);
      }
    });
  };

  const subscribeToFeedCountsForPosts = (postIds: string[]) => {
    const db = getFirestore();
    const q = query(
      collection(db, "feedcounts"),
      where("type", "!=", "post"),
      where("data.sourceId", "in", postIds)
    );

    return onSnapshot(
      q,
      processFeedCountSnapshot,
      (error) => console.error("💥 Firebase listener error:", error)
    );
  };

  const processMyReactionsSnapshot = (querySnapshot: any) => {
    querySnapshot.docChanges().forEach((change: any) => {
      const data = change.doc.data();
      const sourceId = data.sourceId || data.postId;
      const sourceType = data.sourceType || 'post';
      if (!sourceId) return;

      const key = reactionKey(sourceId, sourceType);

      if (change.type === 'removed') {
        myReactions.map.delete(key);
      } else if (data.reactionType) {
        myReactions.map.set(key, data.reactionType);
        optimisticReactions.map.delete(key);
      }

      lastAffectedPostIds.value.add(sourceId);
      reactionCountsVersion.value++;
    });
  };

  const subscribeToMyReactionsForPosts = (postIds: string[]) => {
    const userId = storageService.getUser()?._id;
    if (!userId || postIds.length === 0) return [];

    const db = getFirestore();
    return chunkPostIds(postIds).flatMap((batch) => [
      onSnapshot(
        query(
          collection(db, 'reactions'),
          where('userId', '==', userId),
          where('postId', 'in', batch)
        ),
        processMyReactionsSnapshot,
        (error) => console.error('💥 Firebase my-reactions listener error:', error)
      ),
      onSnapshot(
        query(
          collection(db, 'reactions'),
          where('userId', '==', userId),
          where('sourceId', 'in', batch)
        ),
        processMyReactionsSnapshot,
        (error) => console.error('💥 Firebase my-reactions listener error:', error)
      ),
    ]);
  };

  const appendPostReactionListeners = (postIds: string[]) => {
    const newPostIds = postIds.filter((id) => !subscribedPostIds.value.has(id));
    if (newPostIds.length === 0) return;

    newPostIds.forEach((id) => subscribedPostIds.value.add(id));

    const newUnsubscribers = [
      ...chunkPostIds(newPostIds).map((batch) => subscribeToFeedCountsForPosts(batch)),
      ...subscribeToMyReactionsForPosts(newPostIds),
    ];

    unsubscribeReactions.value.push(...newUnsubscribers);
  };

  // Getters

  const getReactionCount = computed(() => (sourceId: string, sourceType: string): ReactionCountModel | null => {
    const key = `${sourceId}-${sourceType}`;
    return reactiveReactionCounts.map.get(key) || null;
  });

  const getCommentCount = computed(() => (sourceId: string, sourceType: string): CommentCountModel | null => {
    const key = `${sourceId}-${sourceType}`;
    return reactiveCommentCounts.map.get(key) || null;
  });

  const getRepostCount = computed(() => (postId: string): RepostCountModel | null => {
    return reactiveRepostCounts.map.get(postId) || null;
  });

  const getPollCount = computed(() => (postId: string): PollCountModel | null => {
    return reactivePollCounts.map.get(postId) || null;
  });

  const getAllReactionCounts = computed(() => {
    const map = reactiveReactionCounts.map;
    return Array.from(map.values());
  });

  const getAllCommentCounts = computed(() => {
    return Array.from(reactiveCommentCounts.map.values());
  });

  const getAllRepostCounts = computed(() => {
    return Array.from(reactiveRepostCounts.map.values());
  });

  const getAllPollCounts = computed(() => {
    return Array.from(reactivePollCounts.map.values());
  });
  const hasUserReacted = computed(() => (sourceId: string, sourceType: string): boolean => {
    if (!storageService.getUser()) return false;

    const key = reactionKey(sourceId, sourceType);
    if (optimisticReactions.map.has(key)) return true;
    return myReactions.map.has(key);
  });

  const getUserReactionType = computed(() => (sourceId: string, sourceType: string): string | null => {
    if (!storageService.getUser()) return null;

    const key = reactionKey(sourceId, sourceType);
    const optimistic = optimisticReactions.map.get(key);
    if (optimistic) return optimistic.reactionType;
    return myReactions.map.get(key) || null;
  });


  const getAllReactionCountsForPost = computed(() => (sourceId: string) => {
    const key = `${sourceId}-post`;
    const reaction = reactiveReactionCounts.map.get(key);

    if (reaction?.reactionCounts) {
      return { ...reaction.reactionCounts };
    }

    return {};
  });
  // Actions
  const initReactionListeners = (feedId: string) => {
    if (currentFeedId.value === feedId && unsubscribeReactions.value.length > 0) {
      return;
    }

    cleanupReactionListeners();
    currentFeedId.value = feedId;

    const db = getFirestore();
    const q = query(
      collection(db, "feedcounts"),
      where("type", "!=", "post")
    );

    unsubscribeReactions.value = [
      onSnapshot(
        q,
        processFeedCountSnapshot,
        (error) => console.error("💥 Firebase listener error:", error)
      ),
    ];
  };

  const initReactionListenersForPostsReactions = (postIds: string[] = []) => {
    if (postIds.length === 0) return;

    if (currentFeedId.value === null) {
      currentFeedId.value = storageService.getUser()?._id;
    }

    appendPostReactionListeners(postIds);
  };

  const initReactionListenersForPosts = (feedId: string, postIds: string[] = []) => {
    if (postIds.length === 0) return;

    currentFeedId.value = feedId;
    appendPostReactionListeners(postIds);
  };

  const cleanupReactionListeners = () => {
    unsubscribeReactions.value.forEach((unsubscribe) => unsubscribe());
    unsubscribeReactions.value = [];
    subscribedPostIds.value.clear();
    currentFeedId.value = null;
  };

  // Update the handleReactionCountChange function to use reactive maps
  const handleReactionCountChange = (activityData: any, changeType: string) => {

    const tempReactionCount = new ReactionCountModel();
    Object.assign(tempReactionCount, activityData.data);
    tempReactionCount._id = activityData.data._id;
    delete (tempReactionCount as any).userIds;

    const key = reactionKey(
      tempReactionCount.sourceId!,
      tempReactionCount.sourceType!
    );

    if (tempReactionCount.sourceId) {
      lastAffectedPostIds.value.add(tempReactionCount.sourceId);
    }

    // Directly modify the existing map - no need to create a new one
    switch (changeType) {
      case "added":
      case "modified":
        {
          // console.log(`handleReactionCountChange added:${tempReactionCount.sourceId}`,tempReactionCount);
          reactiveReactionCounts.map.set(key, tempReactionCount);
          break;
        }

      case "removed":
        reactiveReactionCounts.map.delete(key);
        break;
    }

    // Just increment the version - Vue will detect the change through the version counter
    reactionCountsVersion.value++;

    // console.log("✅ Reaction changed. Version:", reactionCountsVersion.value);
  };

  const handleCommentCountChange = (activityData: any, changeType: string) => {
    const tempCommentCount = new CommentCountModel();
    Object.assign(tempCommentCount, activityData.data);
    tempCommentCount._id = activityData.data._id;

    const key = `${tempCommentCount.sourceId}-${tempCommentCount.sourceType}`;

    if (tempCommentCount.sourceId) {
      lastAffectedPostIds.value.add(tempCommentCount.sourceId);
    }

    // FIX: Properly update the reactive map
    switch (changeType) {
      case "added":
      case "modified":
        reactiveCommentCounts.map.set(key, tempCommentCount);
        break;
      case "removed":
        reactiveCommentCounts.map.delete(key);
        break;
    }

    // FIX: Force reactivity by updating the version
    reactionCountsVersion.value++;
  };

  const handleRepostCountChange = (activityData: any, changeType: string) => {
    const tempRepostCount = new RepostCountModel();
    Object.assign(tempRepostCount, activityData.data);
    tempRepostCount._id = activityData.data._id;

    if (tempRepostCount.postId) {
      lastAffectedPostIds.value.add(tempRepostCount.postId);
    }

    // FIX: Properly update the reactive map
    switch (changeType) {
      case "added":
      case "modified":
        reactiveRepostCounts.map.set(tempRepostCount.postId, tempRepostCount);
        break;
      case "removed":
        reactiveRepostCounts.map.delete(tempRepostCount.postId);
        break;
    }

    // FIX: Force reactivity by updating the version
    reactionCountsVersion.value++;
  };

  const handlePollCountChange = (activityData: any, changeType: string) => {
    const tempPollCount = new PollCountModel();
    Object.assign(tempPollCount, activityData.data);

    switch (changeType) {
      case "added":
      case "modified":
        reactivePollCounts.map.set(tempPollCount.postId, tempPollCount);
        reactivePollCounts.map = new Map(reactivePollCounts.map);
        break;
      case "removed":
        reactivePollCounts.map.delete(tempPollCount.postId);
        reactivePollCounts.map = new Map(reactivePollCounts.map);
        break;
    }
  };

  const addOptimisticReaction = (sourceId: string, sourceType: string, reactionType: string) => {
    if (!storageService.getUser()) return;

    const key = reactionKey(sourceId, sourceType);
    const prevType = myReactions.map.get(key) || optimisticReactions.map.get(key)?.reactionType;

    optimisticReactions.map.set(key, { reactionType, timestamp: Date.now() });
    myReactions.map.set(key, reactionType);

    let reaction = reactiveReactionCounts.map.get(key);
    if (!reaction) {
      reaction = new ReactionCountModel();
      reaction._id = key;
      reaction.sourceId = sourceId;
      reaction.sourceType = sourceType;
      reaction.reactionCounts = {};
      reaction.count = 0;
    }

    if (!reaction.reactionCounts) {
      reaction.reactionCounts = {};
    }

    if (prevType && prevType !== reactionType) {
      reaction.reactionCounts[prevType] = Math.max(
        0,
        (reaction.reactionCounts[prevType] || 0) - 1
      );
      if (reaction.reactionCounts[prevType] === 0) {
        delete reaction.reactionCounts[prevType];
      }
      reaction.reactionCounts[reactionType] =
        (reaction.reactionCounts[reactionType] || 0) + 1;
    } else if (!prevType) {
      reaction.reactionCounts[reactionType] =
        (reaction.reactionCounts[reactionType] || 0) + 1;
    } else {
      return;
    }

    lastAffectedPostIds.value.add(sourceId);
    reaction.count = Object.values(reaction.reactionCounts).reduce(
      (sum, count) => sum + (Number(count) || 0),
      0
    );

    reactiveReactionCounts.map.set(key, reaction);
    reactionCountsVersion.value++;
  };

  const removeOptimisticReaction = (sourceId: string, sourceType: string) => {
    if (!storageService.getUser()) return;

    const key = reactionKey(sourceId, sourceType);
    const prevType = myReactions.map.get(key) || optimisticReactions.map.get(key)?.reactionType;

    optimisticReactions.map.delete(key);
    myReactions.map.delete(key);

    const reaction = reactiveReactionCounts.map.get(key);
    if (reaction?.reactionCounts && prevType) {
      reaction.reactionCounts[prevType] = Math.max(
        0,
        (reaction.reactionCounts[prevType] || 0) - 1
      );
      if (reaction.reactionCounts[prevType] === 0) {
        delete reaction.reactionCounts[prevType];
      }
      reaction.count = Object.values(reaction.reactionCounts).reduce(
        (sum, count) => sum + (Number(count) || 0),
        0
      );
      reactiveReactionCounts.map.set(key, reaction);
    }

    lastAffectedPostIds.value.add(sourceId);
    reactionCountsVersion.value++;
  };

  const likePost = async (likePostData: LikePostModel) => {
    try {
      addOptimisticReaction(
        likePostData.sourceId!,
        likePostData.sourceType!,
        likePostData.reactionType!
      );
      trackEvent(TrackingEvents.reactionAdded, {
        sourceId: likePostData.sourceId,
        reactionType: likePostData.reactionType
      });
      
      initReactionListenersForPostsReactions([likePostData.sourceId]);
      const response = await httpService.likePost(likePostData);

      if (response.data.isSuccess) {
        trackEvent(TrackingEvents.reactionSuccess, {
          sourceId: likePostData.sourceId,
          reactionType: likePostData.reactionType
        });

        // The Firebase snapshot will eventually update the real data
        // We keep the optimistic update until the real data comes in
        return true;
      } else {
        // Revert optimistic update on failure
        removeOptimisticReaction(likePostData.sourceId, likePostData.sourceType);
        trackError(TrackingEvents.reactionFailed, {
          sourceId: likePostData.sourceId,
          reason: response.data.message
        });
        utilService.showToast(response.data.message, TYPE.ERROR);

        return false;
      }
    } catch (error: any) {
      // Revert optimistic update on error
      removeOptimisticReaction(likePostData.sourceId, likePostData.sourceType);
      trackError(TrackingEvents.reactionFailed, {
        sourceId: likePostData.sourceId,
        error: error.message
      });
      utilService.showToast(error.message);

      // throw error;
    }
  };

  const unlikePost = async (unlikeData: UnlikeModel) => {
    try {
      // Optimistic update - immediately remove the reaction
      removeOptimisticReaction(unlikeData.sourceId, unlikeData.sourceType);

      trackEvent(TrackingEvents.reactionRemoved, {
        sourceId: unlikeData.sourceId
      });

      const response = await httpService.unlikePost(unlikeData);

      if (response.data.isSuccess) {
        trackEvent(TrackingEvents.reactionRemoveSuccess, {
          sourceId: unlikeData.sourceId
        });
        return true;
      } else {
        // If unlike fails, we need to restore the reaction
        // This is more complex as we need to know what the previous reaction was
        // For now, we'll let the next Firebase snapshot correct it
        trackError(TrackingEvents.reactionRemoveFailed, {
          sourceId: unlikeData.sourceId,
          reason: response.data.message
        });
        return false;
      }
    } catch (error: any) {
      // If unlike fails, the next Firebase snapshot will correct the state
      trackError(TrackingEvents.reactionRemoveFailed, {
        sourceId: unlikeData.sourceId,
        error: error.message
      });
      utilService.showToast(error.message, TYPE.ERROR);

      throw error;
    }
  };

  const voteOnPoll = async (data: any) => {
    try {
      addToPollCount(data);

      const response = await httpService.votePollStock(data);

      if (!response.data.isSuccess) {
        reactivePollCounts.map.delete(data.postId);
        reactivePollCounts.map = new Map(reactivePollCounts.map);
        throw new Error(response.data.message || 'Failed to vote on poll');
      }

      return response;
    } catch (error: any) {
      trackError(TrackingEvents.pollVoteFailed, {
        postId: data.postId,
        error: error.message
      });
      throw error;
    }
  };

  const addToPollCount = (data: any) => {
    const tempPollCount = new PollCountModel();
    tempPollCount.postId = data.postId;
    tempPollCount.sourceId = data.postId;
    tempPollCount.sourceType = "post";
    tempPollCount.userIds = [{ userId: data.userId, stockId: data.stockId }];
    tempPollCount.count = tempPollCount.userIds.length;

    reactivePollCounts.map.set(data.postId, tempPollCount);
    reactivePollCounts.map = new Map(reactivePollCounts.map);
  };

  const reset = () => {
    cleanupReactionListeners();
    optimisticReactions.map.clear();
    myReactions.map.clear();
    reactiveReactionCounts.map.clear();
    reactiveReactionCounts.map = new Map(reactiveReactionCounts.map);
    reactiveCommentCounts.map.clear();
    reactiveCommentCounts.map = new Map(reactiveCommentCounts.map);
    reactiveRepostCounts.map.clear();
    reactiveRepostCounts.map = new Map(reactiveRepostCounts.map);
    reactivePollCounts.map.clear();
    reactivePollCounts.map = new Map(reactivePollCounts.map);
  };

  const getAndClearAffectedPostIds = () => {
    const affectedIds = new Set(lastAffectedPostIds.value);
    // console.log("Affected post IDs:", Array.from(affectedIds));
    lastAffectedPostIds.value.clear();
    return affectedIds;
  };

  return {
    // State - Return the reactive maps for external access if needed
    reactionCounts: reactiveReactionCounts.map,
    commentCounts: reactiveCommentCounts.map,
    repostCounts: reactiveRepostCounts.map,
    pollCounts: reactivePollCounts.map,
    unsubscribeReactions,
    currentFeedId,
    optimisticReactions,
    myReactions,
    reactionCountsVersion,


    // Getters
    getReactionCount,
    getCommentCount,
    getRepostCount,
    getPollCount,
    hasUserReacted,
    getUserReactionType,
    getAllReactionCounts,
    getAllCommentCounts,
    getAllRepostCounts,
    getAllPollCounts,
    getAllReactionCountsForPost,
    getAndClearAffectedPostIds,


    // Actions
    initReactionListenersForPostsReactions,
    initReactionListenersForPosts,
    initReactionListeners,
    cleanupReactionListeners,
    likePost,
    unlikePost,
    voteOnPoll,
    reset
  };
});